import { createRandomToken } from "./encoding";
import { hashPassword, validatePasswordStrength, verifyPassword } from "./passwords";
import {
  type AuthUser,
  createSession,
  hashSessionToken,
  revokeSession,
  type SessionStore,
  verifySession
} from "./sessions";

export interface AuthUserRecord extends AuthUser {
  passwordHash?: string | null;
}

export interface AuthStore extends SessionStore {
  findUserByEmail(email: string): Promise<AuthUserRecord | undefined>;
  findUserById(userId: string): Promise<AuthUserRecord | undefined>;
  createUser(input: {
    email: string;
    name?: string;
    role: AuthUser["role"];
    organizationId?: string;
    passwordHash?: string | null;
    isActive?: boolean;
  }): Promise<AuthUserRecord>;
  updateUser(input: { userId: string; role?: AuthUser["role"]; isActive?: boolean; passwordHash?: string | null }): Promise<void>;
  insertPasswordResetToken(input: {
    id: string;
    userId: string;
    tokenHash: string;
    expiresAt: string;
    createdAt: string;
  }): Promise<void>;
  findPasswordResetToken(tokenHash: string): Promise<{ id: string; userId: string; expiresAt: string; usedAt?: string } | undefined>;
  markPasswordResetTokenUsed(id: string, usedAt: string): Promise<void>;
  recordAuthAttempt(input: { email: string; ipHash?: string; success: boolean; createdAt: string }): Promise<void>;
}

export interface LoginResult {
  ok: boolean;
  message?: string;
  user?: AuthUser;
  sessionToken?: string;
  sessionExpiresAt?: string;
}

export function isDemoAuthEnabled(value: string | undefined): boolean {
  return value === "true";
}

export async function loginWithPassword(
  store: AuthStore,
  input: { email: string; password: string; ipHash?: string },
  now = new Date()
): Promise<LoginResult> {
  const email = input.email.trim().toLowerCase();
  const user = await store.findUserByEmail(email);
  const passwordValid = await verifyPassword(input.password, user?.passwordHash);
  const success = Boolean(user?.isActive && passwordValid);

  await store.recordAuthAttempt({ email, ipHash: input.ipHash, success, createdAt: now.toISOString() });

  if (!user || !passwordValid) {
    return { ok: false, message: "E-Mail oder Passwort ist nicht korrekt." };
  }
  if (!user.isActive) {
    return { ok: false, message: "Dieser Zugang ist aktuell nicht aktiv." };
  }

  const created = await createSession(user.id, store, now);
  return {
    ok: true,
    user: sanitizeUser(user),
    sessionToken: created.token,
    sessionExpiresAt: created.session.expiresAt
  };
}

export async function logout(store: AuthStore, sessionToken: string | undefined, now = new Date()): Promise<void> {
  await revokeSession(sessionToken, store, now);
}

export async function getCurrentUser(store: AuthStore, sessionToken: string | undefined, now = new Date()): Promise<AuthUser | undefined> {
  return verifySession(sessionToken, store, now);
}

export async function requestPasswordReset(
  store: AuthStore,
  email: string,
  now = new Date()
): Promise<{ token?: string; prepared: boolean }> {
  const user = await store.findUserByEmail(email.trim().toLowerCase());
  if (!user || !user.isActive) {
    return { prepared: true };
  }

  const token = createRandomToken(32);
  const tokenHash = await hashSessionToken(token);
  await store.insertPasswordResetToken({
    id: crypto.randomUUID(),
    userId: user.id,
    tokenHash,
    expiresAt: new Date(now.getTime() + 60 * 60 * 1000).toISOString(),
    createdAt: now.toISOString()
  });
  return { token, prepared: true };
}

export async function resetPassword(
  store: AuthStore,
  token: string,
  password: string,
  now = new Date()
): Promise<{ ok: boolean; message: string }> {
  const validation = validatePasswordStrength(password);
  if (!validation.ok) return { ok: false, message: validation.message ?? "Passwort ist nicht ausreichend sicher." };

  const tokenHash = await hashSessionToken(token);
  const resetToken = await store.findPasswordResetToken(tokenHash);
  if (!resetToken || resetToken.usedAt || new Date(resetToken.expiresAt).getTime() <= now.getTime()) {
    return { ok: false, message: "Der Passwort-Link ist ungültig oder abgelaufen." };
  }

  await store.updateUser({ userId: resetToken.userId, passwordHash: await hashPassword(password) });
  await store.markPasswordResetTokenUsed(resetToken.id, now.toISOString());
  return { ok: true, message: "Passwort wurde aktualisiert." };
}

export function getDemoSessionIfAllowed(email: string, enableDemoAuth: string | undefined) {
  if (!isDemoAuthEnabled(enableDemoAuth)) return undefined;
  const demoSessions: Record<string, { userId: string; email: string; role: AuthUser["role"]; name: string }> = {
    "torben@hauffe.io": {
      userId: "user-hauffe-admin",
      email: "torben@hauffe.io",
      role: "hauffe_admin",
      name: "Torben Hauffe"
    },
    "presse@hauffe.io": {
      userId: "user-company-owner",
      email: "presse@hauffe.io",
      role: "company_owner",
      name: "Presse Demo"
    },
    "torben.hauffe@gmail.com": {
      userId: "user-department-sales",
      email: "torben.hauffe@gmail.com",
      role: "department_contributor",
      name: "Abteilungsperspektive"
    }
  };
  return demoSessions[email.trim().toLowerCase()];
}

function sanitizeUser(user: AuthUserRecord): AuthUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
    organizationId: user.organizationId,
    isActive: user.isActive
  };
}
